import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { pole } from '../utils';
import ProdejnaCombobox from './ProdejnaCombobox';
import { urciPrioritu } from '../priorita';
import { inputStyle, selectStyle, labelStyle, btnPrimary, btnOutline, card, chyboveHlaseni, apiChyba, naLocalInput, zLocalInput } from '../ui';
import { DateTimeInput } from './DateInput';

const API = process.env.REACT_APP_API_URL || '/api';

// Nový tiket. `navazujeNaId` předvyplní zákazníka, prodejnu a popis (navazující tiket).
export default function NovyTiket({ setStranka, naviguj, navazujeNaId }) {
  const [zakaznici, setZakaznici] = useState([]);
  const [priority, setPriority] = useState([]);
  const [pravidla, setPravidla] = useState([]);
  const [kriticka, setKriticka] = useState(false);  // „Kritická závada“; nezaškrtnuto = běžná
  const [resitele, setResitele] = useState([]);
  const [predchozi, setPredchozi] = useState(null);
  const [f, setF] = useState({
    zakaznik_id: '', obj: '', prijato_at: naLocalInput(),
    popis_problemu: '', kontakt_jmeno: '', kontakt_telefon: '', resitel_id: '',
  });
  const [prodejna, setProdejna] = useState({ provozovnaId: '', nova: null });
  const [vychoziProdejna, setVychoziProdejna] = useState('');
  const [pokus, setPokus] = useState(false);  // po prvním pokusu o uložení se zvýrazňují chybějící pole
  const [chyba, setChyba] = useState(null);
  const [duplicita, setDuplicita] = useState(null);
  const [ukladam, setUkladam] = useState(false);

  useEffect(() => {
    Promise.all([axios.get(`${API}/zakaznici`), axios.get(`${API}/priority`), axios.get(`${API}/resitele`), axios.get(`${API}/sla-pravidla`)])
      .then(([z, p, r, sla]) => { setZakaznici(pole(z.data)); setPriority(pole(p.data)); setResitele(pole(r.data)); setPravidla(pole(sla.data)); })
      .catch(e => setChyba(apiChyba(e, 'Nepodařilo se načíst číselníky.')));
  }, []);

  useEffect(() => {
    if (!navazujeNaId) return;
    axios.get(`${API}/tikety/${navazujeNaId}`).then(r => {
      const t = r.data;
      setPredchozi(t);
      setProdejna({ provozovnaId: String(t.provozovna_id), nova: null });
      setVychoziProdejna([t.provozovna_nazev, t.provozovna_adresa, t.provozovna_mesto].filter(Boolean).join(' · '));
      setF(x => ({ ...x, zakaznik_id: String(t.zakaznik_id), obj: t.obj,
        popis_problemu: t.popis_problemu, kontakt_jmeno: t.kontakt_jmeno, kontakt_telefon: t.kontakt_telefon }));
    }).catch(e => setChyba(apiChyba(e, 'Nepodařilo se načíst původní tiket.')));
  }, [navazujeNaId]);

  const zakaznik = zakaznici.find(z => String(z.id) === String(f.zakaznik_id));

  // Priorita = zaškrtávátko „Kritická závada“ (výchozí běžná). Zákazníkovi s jedinou prioritou
  // nebo bez lhůt se zaškrtávátko nezobrazí a použije se běžná.
  const pr = urciPrioritu(priority, pravidla, f.zakaznik_id);
  const kritickaEfekt = pr.zobrazit && kriticka;
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const nastavZakaznika = v => { setF(x => ({ ...x, zakaznik_id: v })); setProdejna({ provozovnaId: '', nova: null }); setKriticka(false); };

  // Konkrétní chybějící pole (zobrazí se ve výpisu i jako červený rámeček)
  const chybi = [];
  const kontrola = (klic, label, ok) => { if (!ok) chybi.push({ klic, label }); };
  kontrola('zakaznik', 'Zákazník', !!f.zakaznik_id);
  kontrola('prodejna', 'Prodejna', !!(prodejna.provozovnaId || prodejna.nova));
  kontrola('obj', 'Číslo objednávky (OBJ)', !!f.obj.trim());
  kontrola('prijato', 'Datum a čas přijetí', !!f.prijato_at);
  kontrola('popis', 'Popis problému', !!f.popis_problemu.trim());
  kontrola('kjmeno', 'Kontakt – jméno', !!f.kontakt_jmeno.trim());
  kontrola('ktel', 'Kontakt – telefon', !!f.kontakt_telefon.trim());
  const chybne = klic => pokus && chybi.some(c => c.klic === klic);
  const rameckoPole = (klic, zaklad = inputStyle) => ({ ...zaklad, ...(chybne(klic) ? { border: '2px solid #ef4444', background: '#fef2f2' } : {}) });

  const uloz = async () => {
    setChyba(null); setDuplicita(null);
    const priorita = (kritickaEfekt ? pr.kriticka : pr.bezna || priority[0])?.id;
    setPokus(true);
    if (chybi.length) { setChyba(`Chybí: ${chybi.map(c => c.label).join(', ')}`); return; }
    if (!priorita) { setChyba('V číselníku chybí priority – kontaktujte admina.'); return; }
    setUkladam(true);
    try {
      const r = await axios.post(`${API}/tikety`, {
        zakaznik_id: Number(f.zakaznik_id), priorita_id: Number(priorita),
        ...(prodejna.nova ? { nova_provozovna: { nazev: prodejna.nova.nazev, mesto: prodejna.nova.mesto || null } } : { provozovna_id: Number(prodejna.provozovnaId) }),
        obj: f.obj.trim(), prijato_at: zLocalInput(f.prijato_at), popis_problemu: f.popis_problemu.trim(),
        kontakt_jmeno: f.kontakt_jmeno.trim(), kontakt_telefon: f.kontakt_telefon.trim(),
        resitel_id: f.resitel_id ? Number(f.resitel_id) : null,
        navazuje_na_tiket_id: navazujeNaId || null,
      });
      naviguj('tiket-detail', r.data.id);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.existujici_tiket_id) setDuplicita(d);
      else setChyba(apiChyba(e));
    }
    setUkladam(false);
  };

  return (
    <div>
      <button onClick={() => setStranka('rozcesti')} style={{ ...btnOutline, marginBottom: '14px' }}>← Zpět</button>
      <h2 style={{ margin: '0 0 12px', color: '#333' }}>{navazujeNaId ? 'Navazující tiket' : 'Nový tiket'}</h2>
      {predchozi && (
        <div style={{ ...card, background: '#f0fdf4' }}>
          Navazuje na tiket <strong>{predchozi.obj}</strong> ({predchozi.zakaznik_nazev}, {predchozi.provozovna_nazev}).
          Převezme OBJ, zákazníka a prodejnu původního tiketu; má vlastní čas přijetí a SLA.
        </div>
      )}
      <div style={card}>
        <label style={labelStyle}>Zákazník *</label>
        <select style={rameckoPole('zakaznik', selectStyle)} value={f.zakaznik_id} onChange={e => nastavZakaznika(e.target.value)} disabled={!!navazujeNaId}>
          <option value="">— vyberte —</option>
          {zakaznici.map(z => <option key={z.id} value={z.id}>{z.nazev}</option>)}
        </select>

        <label style={labelStyle}>Prodejna * (hledejte podle názvu, města nebo adresy)</label>
        <ProdejnaCombobox zakaznik={zakaznik} hodnota={prodejna} onChange={setProdejna} chybne={chybne('prodejna')} vychoziText={vychoziProdejna} />

        <label style={labelStyle}>Číslo objednávky (OBJ) *</label>
        <input style={rameckoPole('obj')} value={f.obj} onChange={e => set('obj', e.target.value)} inputMode="numeric" autoComplete="off" disabled={!!navazujeNaId} />
        {duplicita && (
          <div style={chyboveHlaseni}>
            {duplicita.zprava}{' '}
            <button style={{ background: 'none', border: 'none', color: '#1d4ed8', textDecoration: 'underline', cursor: 'pointer', fontSize: '14px', padding: 0 }}
              onClick={() => naviguj('tiket-detail', duplicita.existujici_tiket_id)}>Otevřít existující tiket</button>
          </div>
        )}

        <label style={labelStyle}>Datum a čas přijetí *</label>
        <DateTimeInput style={rameckoPole('prijato')} value={f.prijato_at} onChange={v => set('prijato_at', v)} />
        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>Zadejte čas, kdy OBJ skutečně přišla.</div>

        {pr.zobrazit && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0 0', padding: '12px', cursor: 'pointer',
            border: `2px solid ${kriticka ? '#dc2626' : '#d1d5db'}`, borderRadius: '8px', background: kriticka ? '#fef2f2' : '#fff', fontSize: '16px' }}>
            <input type="checkbox" checked={kriticka} onChange={e => setKriticka(e.target.checked)} style={{ width: '22px', height: '22px' }} />
            <span><strong>Kritická závada</strong><span style={{ display: 'block', fontSize: '12px', color: '#6b7280' }}>Nezaškrtnuto = běžná závada.</span></span>
          </label>
        )}

        <label style={labelStyle}>Popis problému *</label>
        <textarea style={{ ...rameckoPole('popis'), minHeight: '100px' }} value={f.popis_problemu} onChange={e => set('popis_problemu', e.target.value)} />

        <label style={labelStyle}>Kontakt na prodejně – jméno *</label>
        <input style={rameckoPole('kjmeno')} value={f.kontakt_jmeno} onChange={e => set('kontakt_jmeno', e.target.value)} autoComplete="off" />
        <label style={labelStyle}>Kontakt na prodejně – telefon *</label>
        <input style={rameckoPole('ktel')} type="tel" value={f.kontakt_telefon} onChange={e => set('kontakt_telefon', e.target.value)} autoComplete="off" />

        <label style={labelStyle}>Řešitel</label>
        <select style={selectStyle} value={f.resitel_id} onChange={e => set('resitel_id', e.target.value)}>
          <option value="">Nechat ve frontě</option>
          {resitele.map(r => <option key={r.id} value={r.id}>{r.jmeno}</option>)}
        </select>

        {chyba && <div style={chyboveHlaseni}>{chyba}</div>}
        <button style={{ ...btnPrimary, width: '100%', marginTop: '18px' }} onClick={uloz} disabled={ukladam}>
          {ukladam ? 'Ukládám…' : 'Založit tiket'}
        </button>
      </div>
    </div>
  );
}
